// RevenueCat configuration for native IAP (iOS/Android)
import { Purchases, LOG_LEVEL, type PurchasesPackage } from '@revenuecat/purchases-capacitor';
import { isNative, isIOS } from './capacitor';

// RevenueCat API Keys (set via environment variables)
const REVENUECAT_IOS_KEY = import.meta.env.VITE_REVENUECAT_IOS_KEY || '';
const REVENUECAT_ANDROID_KEY = import.meta.env.VITE_REVENUECAT_ANDROID_KEY || '';

// Product identifiers (must match RevenueCat dashboard)
export const REVENUECAT_PRODUCTS = {
  MONTHLY: 'pp_monthly',
  SIX_MONTH: 'pp_6month',
  YEARLY: 'pp_1year',
} as const;

// Entitlement identifier
export const PREMIUM_ENTITLEMENT = 'premium';

// Track initialization state
let isInitialized = false;

/**
 * Initialize RevenueCat SDK
 * Should be called once when app starts on native platforms
 */
export async function initRevenueCat(userId?: string): Promise<void> {
  if (!isNative) {
    console.log('RevenueCat: Skipping init on web platform');
    return;
  }

  if (isInitialized) {
    console.log('RevenueCat: Already initialized');
    return;
  }

  const apiKey = isIOS ? REVENUECAT_IOS_KEY : REVENUECAT_ANDROID_KEY;

  if (!apiKey) {
    console.warn('RevenueCat: No API key configured for platform');
    return;
  }

  try {
    await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });

    await Purchases.configure({
      apiKey,
      appUserID: userId || undefined,
    });

    isInitialized = true;
    console.log('RevenueCat: Initialized successfully');
  } catch (error) {
    console.error('RevenueCat: Initialization failed:', error);
    throw error;
  }
}

/**
 * Log in a user to RevenueCat
 * Call when user authenticates in your app
 */
export async function loginRevenueCat(userId: string): Promise<void> {
  if (!isNative || !isInitialized) return;

  try {
    await Purchases.logIn({ appUserID: userId });
    console.log('RevenueCat: User logged in:', userId);
  } catch (error) {
    console.error('RevenueCat: Login failed:', error);
    throw error;
  }
}

/**
 * Log out current user from RevenueCat
 * Call when user logs out of your app
 */
export async function logoutRevenueCat(): Promise<void> {
  if (!isNative || !isInitialized) return;

  try {
    await Purchases.logOut();
    console.log('RevenueCat: User logged out');
  } catch (error) {
    console.error('RevenueCat: Logout failed:', error);
    throw error;
  }
}

/**
 * Check if user has premium entitlement
 */
export async function checkPremiumEntitlement(): Promise<boolean> {
  if (!isNative || !isInitialized) return false;

  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    const hasEntitlement = customerInfo.entitlements.active[PREMIUM_ENTITLEMENT] !== undefined;
    console.log('RevenueCat: Premium entitlement check:', hasEntitlement);
    return hasEntitlement;
  } catch (error) {
    console.error('RevenueCat: Failed to check entitlement:', error);
    return false;
  }
}

/**
 * Get available offerings (products)
 */
export async function getOfferings() {
  if (!isNative || !isInitialized) {
    throw new Error('RevenueCat not available');
  }

  try {
    const offerings = await Purchases.getOfferings();
    console.log('RevenueCat: Offerings fetched:', offerings);
    return offerings;
  } catch (error) {
    console.error('RevenueCat: Failed to get offerings:', error);
    throw error;
  }
}

/**
 * Extract error details from RevenueCat error object
 * RevenueCat errors have non-enumerable properties that don't serialize well
 */
function extractErrorDetails(error: unknown): { message: string; code?: number; underlyingErrorMessage?: string } {
  if (error instanceof Error) {
    return { message: error.message };
  }

  // RevenueCat errors have specific properties
  const rcError = error as {
    message?: string;
    code?: number;
    underlyingErrorMessage?: string;
    readableErrorCode?: string;
    userInfo?: { readable_error_code?: string; message?: string };
  };

  return {
    message: rcError.message || rcError.readableErrorCode || rcError.userInfo?.message || 'Unknown purchase error',
    code: rcError.code,
    underlyingErrorMessage: rcError.underlyingErrorMessage || rcError.userInfo?.readable_error_code,
  };
}

/**
 * Purchase a package
 */
export async function purchasePackage(packageToPurchase: PurchasesPackage) {
  if (!isNative || !isInitialized) {
    throw new Error('RevenueCat not available');
  }

  try {
    const result = await Purchases.purchasePackage({
      aPackage: packageToPurchase,
    });

    const hasPremium = result.customerInfo.entitlements.active[PREMIUM_ENTITLEMENT] !== undefined;
    console.log('RevenueCat: Purchase completed, has premium:', hasPremium);

    return {
      success: true,
      customerInfo: result.customerInfo,
      hasPremium,
    };
  } catch (error: unknown) {
    // Check if user cancelled
    const purchaseError = error as { userCancelled?: boolean; code?: number };
    if (purchaseError.userCancelled || purchaseError.code === 1) {
      console.log('RevenueCat: Purchase cancelled by user');
      return {
        success: false,
        cancelled: true,
        error: null,
      };
    }

    // Extract and log detailed error information
    const errorDetails = extractErrorDetails(error);
    console.error('RevenueCat: Purchase failed:', JSON.stringify(errorDetails, null, 2));
    console.error('RevenueCat: Raw error object keys:', Object.keys(error as object));

    // Try to get more details from the error
    if (typeof error === 'object' && error !== null) {
      console.error('RevenueCat: Error properties:', {
        message: (error as { message?: string }).message,
        code: (error as { code?: number }).code,
        underlyingErrorMessage: (error as { underlyingErrorMessage?: string }).underlyingErrorMessage,
        readableErrorCode: (error as { readableErrorCode?: string }).readableErrorCode,
      });
    }

    return {
      success: false,
      cancelled: false,
      error: new Error(errorDetails.message),
      errorDetails,
    };
  }
}

/**
 * Restore purchases (for users reinstalling or switching devices)
 */
export async function restorePurchases() {
  if (!isNative || !isInitialized) {
    throw new Error('RevenueCat not available');
  }

  try {
    const { customerInfo } = await Purchases.restorePurchases();
    const hasPremium = customerInfo.entitlements.active[PREMIUM_ENTITLEMENT] !== undefined;
    console.log('RevenueCat: Purchases restored, has premium:', hasPremium);

    return {
      success: true,
      customerInfo,
      hasPremium,
    };
  } catch (error) {
    console.error('RevenueCat: Restore failed:', error);
    throw error;
  }
}

/**
 * Get customer info
 */
export async function getCustomerInfo() {
  if (!isNative || !isInitialized) {
    return null;
  }

  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return customerInfo;
  } catch (error) {
    console.error('RevenueCat: Failed to get customer info:', error);
    return null;
  }
}

/**
 * Check if RevenueCat is available and initialized
 */
export function isRevenueCatAvailable(): boolean {
  return isNative && isInitialized;
}
